import * as React from "react";
import * as ReactDOM from "react-dom";
import { getUsers } from "../../../api/users";
import { getLicence } from "../../../api/settings";
import { IUser, ILicence } from "../../../models/get";
import { Loader, LoaderStyle } from "../../../components/loader";
import { ToolBar, ToolBarItem, ToolBarSeparator } from "../../../components/toolbar";
import { InputSize } from "../../../components/input";
import { TextBox } from "../../../components/textBox";
import { Button, ButtonType, ButtonSize } from "../../../components/button";
import { Table, CheckColumn, ITableColumn, ITableRowEventArgs } from "../../../components/table";
import { Alert, AlertType } from "../../../components/alert";
import SettingsNav from "../nav";
import Dialog from "./dialog";

interface IProperties
{
}

interface IState
{
    /**
     * Gibt an, ob gerade geladen wird.
     */
    isLoading?: boolean,

    /**
     * Gibt an, ob der Dialog geöffnet ist.
     */
    isDialogOpen?: boolean,

    /**
     * Gibt an, ob der Dialog zum bearbeiten eines Datensatzes geöffnet wird.
     */
    isDialogInEditMode?: boolean,

    /**
     * Enthält Text der zum Filtern der Liste verwendet wird.
     */
    filterValue?: string,

    /**
     * Enthält die geladenen Daten.
     */
    users?: IUser[],

    /**
     * Enthält die gefilterten Daten.
     */
    filteredUsers?: IUser[],

    /**
     * Enthält den momentan selektierten Datensatz.
     */
    selectedItem?: IUser,

    /**
     * Enthält den momentan selektierten Zeilenindex.
     */
    selectedRowIndex?: string,

    /**
     * Enthält die registrierte Lizenz des Kunden.
     */
    licence?: ILicence,

    /**
     * Angabe ob alle Lizenzen aufgebraucht sind.
     */
    numberOfLeftLicences?: number,

    /**
     * Der Text der Meldungen welche angezeigt werden soll.
     */
    alertMessage?: string,

    /**
     * Der Typ der Meldung.
     */
    alertType?: AlertType
}

export class UserSettings extends React.Component<IProperties, IState>
{
    /**
     * Erstellt eine neue Instanz der Klasse @see UsersList.
     * @param props Alle gesetzten Eigenschaften.
     */
    constructor(props: IProperties)
    {
        super(props);

        this.state = {
            isLoading: true
        }

        this.initAsync(props);
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        var users = await getUsers();
        var licence = await getLicence();

        
        this.setState((state: IState) =>
        {
            state.isLoading = false;
            state.users = users;
            state.filteredUsers = users;

            let activeUsers: number = users.where(u => !u.is_deactivated).length;
            if (licence.numberOfLicences > 0)
            {
                if (licence.numberOfLicences > activeUsers)
                {
                    state.numberOfLeftLicences = licence.numberOfLicences - activeUsers;
                    state.alertType = AlertType.info;

                    if (state.numberOfLeftLicences === 1)
                        state.alertMessage = `Es steht noch ${state.numberOfLeftLicences} Lizenz zur Verfügung.`;
                    else
                        state.alertMessage = `Es stehen noch ${state.numberOfLeftLicences} Lizenzen zur Verfügung.`;
                }
                else if (licence.numberOfLicences <= activeUsers)
                {
                    state.numberOfLeftLicences = 0;
                    state.alertType = AlertType.warning;
                    state.alertMessage = `Ihre ${licence.numberOfLicences} Lizenzen sind aufgebraucht. Um einen neuen Benutzer anlegen zu können müssen Sie einen Benutzer löschen oder deaktivieren.`;
                }
            }

            return state;
        });
        this.setState({
            isLoading: false,
            users: users,
            filteredUsers: users,
            licence: licence
        });
    }

    /**
     * Aktualisiert die Liste.
     */
    private async refreshAsync()
    {
        this.setState({
            isLoading: true,
            filterValue: null
        });

        await this.initAsync(this.props);
    }

    /**
     * Wird ausgeführt, sobald sich der Text der Filter TextBox ändert.
     * @param value Der konvertierte Wert der TextBox.
     * @param originalValue Der originale Wert der TextBox.
     */
    private onFilterTextChanged = (value: any[], originalValue: string[]): void =>
    {
        const { users } = this.state;
        var filter: string = value[0];
        var filteredData: IUser[] = [];

        if (filter != null && filter !== "")
            filteredData = users.where(u => u.fullname && u.fullname.toLowerCase().indexOf(filter.toLowerCase()) >= 0);
        else
            filteredData = users;

        this.setState({
            filterValue: value[0],
            filteredUsers: filteredData
        });
    }

    /**
     * Wird ausgeführt sobald auf eine Zeile geklickt wird. Die Daten der ausgewählten
     * Zeile werden hier für weitere Zwecke zwischengespeichert.
     * @param e
     */
    private onRowClick = (e: ITableRowEventArgs<any>) =>
    {
        this.setState({
            selectedItem: e.dataSource,
            selectedRowIndex: e.index
        });
    }

    /**
     * Öffnet den Dialog zum erstellen eines neuen Datensatzes.
     */
    private onNewClick = () =>
    {
        this.setState({
            isDialogOpen: true,
            isDialogInEditMode: false
        });
    }

    /**
     * Öffnet den Dialog zum bearbeiten eines Datensatzes.
     */
    private onEditClick = () =>
    {
        this.setState({
            isDialogOpen: true,
            isDialogInEditMode: true
        });
    }

    /**
     * Wird aufgerufen, sobald sich der Dialog schließt.
     * @param refresh Angabe, ob die Ansicht aktualisiert werden soll.
     */
    private onDialogClosed = async (refresh: boolean) =>
    {
        if (refresh)
        {
            await this.refreshAsync();
            this.setState({
                isDialogOpen: false,
                selectedItem: null,
                selectedRowIndex: null
            });
        }
        else
        {
            this.setState({
                isDialogOpen: false
            });
        }
    }

    /**
     * Render einen InfoText wenn die Liste leer ist.
     */
    private renderInfoText(): JSX.Element
    {
        return (
            <div className="settings-default_empty">
                <i className="settings-default_empty_icon mdi mdi-account"></i>
                <div className="settings-default_empty_header">Benutzer</div>
                <div
                    style={{
                        width: 370
                    }}
                    className="settings-default_empty_text">
                    Benutzer anlegen.
                </div>
                <Button
                    icon="plus"
                    text="Benutzer"
                    type={ButtonType.primary}
                    size={ButtonSize.big}
                    onClick={this.onNewClick} />
            </div>
        );
    }

    /**
     * Rendert die Tabelle.
     */
    private renderTable(): JSX.Element
    {
        const { users, filteredUsers } = this.state;
        const columns: ITableColumn[] = [
            {
                header: "E-Mail",
                accessor: "email",
                width: 200
            },
            {
                header: "Name",
                accessor: "fullname",
                width: 200
            },
            {
                header: "Gruppe",
                accessor: "group_name",
                width: 200
            },
            {
                header: "Bestatter",
                accessor: "mortician_fullname",
                width: 200
            },
            {
                header: "Deaktiviert",
                cell: (user: IUser) => <CheckColumn isChecked={user.is_deactivated} width={100} />,
                width: 100
            }
        ];

        if (users != null && users.length > 0)
        {
            return (
                <Table
                    style={{
                        position: "relative"
                    }}
                    dataSource={filteredUsers}
                    columns={columns}
                    keyAccessor="id"
                    onRowClick={this.onRowClick} />
            );
        }
        else
        {
            return this.renderInfoText();
        }
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const { isLoading, isDialogOpen, isDialogInEditMode, filterValue, users, selectedItem, licence, numberOfLeftLicences, alertType, alertMessage } = this.state;
        const isListEmpty: boolean = users == null || users.length === 0;
        const isItemSelected: boolean = selectedItem != null;

        if (isLoading)
        {
            return (
                <Loader
                    style={{ marginTop: 40 }}
                    loaderStyle={LoaderStyle.style1}
                    infoText="Benutzer werden geladen" />
            );
        }
        
        return (
            <div className="settings-content">
                <div className="settings-content_container">
                    <div className="settings-content_nav">
                        <SettingsNav selected="users" />
                    </div>
                    <div className="settings-default-container">
                        {
                            !isListEmpty &&
                            <div className="settings-default_toolbar">
                                <ToolBar hasTopBorder={false}>
                                    <ToolBarItem
                                        icon="plus"
                                        text="Neu"
                                        onClick={this.onNewClick}
                                        isDisabled={numberOfLeftLicences === 0} />
                                    <ToolBarSeparator />
                                    <ToolBarItem
                                        icon="pencil"
                                        text="Bearbeiten"
                                        onClick={this.onEditClick}
                                        isDisabled={!isItemSelected} />
                                </ToolBar>
                            </div>
                        }
                        <div className="settings-default_alert">
                            <Alert
                                alertType={alertType}
                                isVisible={alertMessage != null}>
                                {alertMessage}
                            </Alert>
                        </div>
                        {
                            !isListEmpty &&
                            <div className="settings-default_filter">
                                <TextBox
                                    placeholder="Filter Name..."
                                    size={InputSize.normal}
                                    value={filterValue}
                                    onTextChange={this.onFilterTextChanged} />
                            </div>
                        }
                        <div className="settings-default">
                            {this.renderTable()}
                        </div>
                        {
                            isDialogOpen &&
                            <Dialog
                                userId={isDialogInEditMode && isItemSelected && selectedItem.id}
                                isLicenceLimitReached={numberOfLeftLicences === 0}
                                onClose={this.onDialogClosed} />
                        }
                        </div>
                </div>
            </div>
        );
    }
}